import { DonorsResponse } from "../services/donors.service";
import { useEffect, useMemo, useState } from "react";
import { routes } from "@/services/donors.service";
import { fetcher } from "@/lib/apiFetch";
import useSWR, { preload } from "swr";
import { fetcherV2 } from "@/services/api";

interface Props {
  keyword: string | undefined;
  initialPage: number;
  limit: number;
  sortBy: string;
  sortDirection: "ASC" | "DESC";
}

export function useDonors({
  keyword,
  initialPage,
  limit,
  sortBy,
  sortDirection,
}: Props) {
  const [page, setPage] = useState<number>(initialPage);
  const [pages, setPages] = useState<number>(0);

  const { data, error, isLoading } = useSWR(
    () => [routes.donors, { page, limit, sortBy, sortDirection, keyword }],
    fetcherV2<DonorsResponse>,
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      suspense: false,
      shouldRetryOnError: false,
    }
  );

  useEffect(() => {
    if (data && !isLoading) {
      setPages(data.pages);
    }
  }, [data, isLoading]);

  const preloadData = (increment: -1 | 1) => {
    preload(
      [
        routes.donors,
        {
          page: page + increment,
          limit,
          sortBy,
          sortDirection,
          keyword,
        },
      ],
      fetcherV2
    );
  };

  return {
    donors: data?.result ?? [],
    total: data?.total ?? 0,
    page: page + 1,
    pages,
    next: () => setPage((p) => p + 1),
    previous: () => setPage((p) => p - 1),
    canPrevious: page > 0,
    canNext: page + 1 < pages,
    isLoading: !error && !data,
    isError: error,
    pageSize: limit,
    preloadData,
  };
}
